import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { 
  Plane, 
  Ship, 
  Truck, 
  Clock, 
  Shield, 
  Globe,
  ArrowRight,
  Zap,
  Package,
  CheckCircle,
  TrendingUp
} from 'lucide-react';

const Services = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);
  const sectionRef = useRef(null);

  const content = {
    pt: {
      badge: 'NOSSOS SERVIÇOS',
      title: 'Soluções completas em',
      titleHighlight: 'Logística Internacional',
      subtitle: 'Escolha a modalidade ideal para sua operação. Cada solução é personalizada para maximizar eficiência e reduzir custos.',
      cta: 'Ver detalhes',
      services: [
        {
          id: 1,
          icon: Plane,
          title: 'Transporte Aéreo',
          tagline: 'Velocidade e precisão para cargas urgentes',
          description: 'Ideal para mercadorias de alto valor, perecíveis ou com prazo crítico. Entregas para qualquer lugar do mundo.',
          image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
          link: '/transporte-aereo',
          color: 'from-purple-600 to-blue-600',
          stats: [
            { icon: Shield, value: 'Seguro', label: 'Carga protegida' },
            { icon: Globe, value: '150+', label: 'Aeroportos' },
            { icon: Shield, value: '100%', label: 'Rastreável' },
          ],
          benefits: ['Cargas urgentes', 'Alto valor agregado', 'Perecíveis', 'DGR certificado'],
        },
        {
          id: 2,
          icon: Ship,
          title: 'Transporte Marítimo',
          tagline: 'Economia e capacidade para grandes volumes',
          description: 'A melhor relação custo-benefício para cargas de grande volume. FCL, LCL, Break Bulk e cargas projeto.',
          image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&q=80',
          link: '/transporte-maritimo',
          color: 'from-blue-600 to-cyan-500',
          stats: [
            { icon: Package, value: '24Mil', label: '' },
            { icon: Globe, value: '95+', label: 'Países' },
            { icon: TrendingUp, value: '40%', label: 'Economia' },
          ],
          benefits: ['FCL & LCL', 'Break Bulk', 'Cargas Projeto', 'Ro-Ro'],
        },
        {
          id: 3,
          icon: Truck,
          title: 'Transporte Rodoviário',
          tagline: 'Flexibilidade e cobertura nacional',
          description: 'Conexão eficiente entre portos, aeroportos e destinos finais. Cobertura em todo território nacional e Mercosul.',
          image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&q=80',
          link: '/transporte-rodoviario',
          color: 'from-emerald-600 to-teal-500',
          stats: [
            { icon: Zap, value: '24h', label: 'Coleta' },
            { icon: Globe, value: 'Brasil', label: '+ Mercosul' },
            { icon: Shield, value: 'GPS', label: 'Rastreio' },
          ],
          benefits: ['Porta a porta', 'Cargas especiais', 'DTA & MIC-DTA', 'Cross-docking'],
        },
      ],
    },
    en: {
      badge: 'OUR SERVICES',
      title: 'Complete solutions in',
      titleHighlight: 'International Logistics',
      subtitle: 'Choose the ideal modality for your operation. Each solution is customized to maximize efficiency and reduce costs.',
      cta: 'View details',
      services: [
        {
          id: 1,
          icon: Plane,
          title: 'Air Transport',
          tagline: 'Speed and precision for urgent cargo',
          description: 'Ideal for high-value goods, perishables or time-critical shipments. Deliveries anywhere in the world.',
          image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
          link: '/transporte-aereo',
          color: 'from-purple-600 to-blue-600',
          stats: [
            { icon: Shield, value: 'Insured', label: 'Protected cargo' },
            { icon: Globe, value: '150+', label: 'Airports' },
            { icon: Shield, value: '100%', label: 'Trackable' },
          ],
          benefits: ['Urgent cargo', 'High value', 'Perishables', 'DGR certified'],
        },
        {
          id: 2,
          icon: Ship,
          title: 'Sea Transport',
          tagline: 'Economy and capacity for large volumes',
          description: 'The best cost-benefit ratio for large volume cargo. FCL, LCL, Break Bulk and project cargo.',
          image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&q=80',
          link: '/transporte-maritimo',
          color: 'from-blue-600 to-cyan-500',
          stats: [
            { icon: Package, value: '24K', label: '' },
            { icon: Globe, value: '95+', label: 'Countries' },
            { icon: TrendingUp, value: '40%', label: 'Savings' },
          ],
          benefits: ['FCL & LCL', 'Break Bulk', 'Project Cargo', 'Ro-Ro'],
        },
        {
          id: 3,
          icon: Truck,
          title: 'Road Transport',
          tagline: 'Flexibility and national coverage',
          description: 'Efficient connection between ports, airports and final destinations. Coverage throughout Brazil and Mercosur.',
          image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&q=80',
          link: '/transporte-rodoviario',
          color: 'from-emerald-600 to-teal-500',
          stats: [
            { icon: Zap, value: '24h', label: 'Pickup' },
            { icon: Globe, value: 'Brazil', label: '+ Mercosur' },
            { icon: Shield, value: 'GPS', label: 'Tracking' },
          ],
          benefits: ['Door to door', 'Special cargo', 'DTA & MIC-DTA', 'Cross-docking'],
        },
      ],
    },
    es: {
      badge: 'NUESTROS SERVICIOS',
      title: 'Soluciones completas en',
      titleHighlight: 'Logística Internacional',
      subtitle: 'Elija la modalidad ideal para su operación. Cada solución está personalizada para maximizar la eficiencia y reducir costos.',
      cta: 'Ver detalles',
      services: [
        {
          id: 1,
          icon: Plane,
          title: 'Transporte Aéreo',
          tagline: 'Velocidad y precisión para cargas urgentes',
          description: 'Ideal para mercancías de alto valor, perecederos o con plazo crítico. Entregas a cualquier lugar del mundo.',
          image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
          link: '/transporte-aereo',
          color: 'from-purple-600 to-blue-600',
          stats: [
            { icon: Shield, value: 'Seguro', label: 'Carga protegida' },
            { icon: Globe, value: '150+', label: 'Aeropuertos' },
            { icon: Shield, value: '100%', label: 'Rastreable' },
          ],
          benefits: ['Cargas urgentes', 'Alto valor', 'Perecederos', 'DGR certificado'],
        },
        {
          id: 2,
          icon: Ship,
          title: 'Transporte Marítimo',
          tagline: 'Economía y capacidad para grandes volúmenes',
          description: 'La mejor relación costo-beneficio para cargas de gran volumen. FCL, LCL, Break Bulk y cargas proyecto.',
          image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&q=80',
          link: '/transporte-maritimo',
          color: 'from-blue-600 to-cyan-500',
          stats: [
            { icon: Package, value: '24Mil', label: '' },
            { icon: Globe, value: '95+', label: 'Países' },
            { icon: TrendingUp, value: '40%', label: 'Ahorro' },
          ],
          benefits: ['FCL & LCL', 'Break Bulk', 'Carga Proyecto', 'Ro-Ro'],
        },
        {
          id: 3,
          icon: Truck,
          title: 'Transporte Terrestre',
          tagline: 'Flexibilidad y cobertura nacional',
          description: 'Conexión eficiente entre puertos, aeropuertos y destinos finales. Cobertura en todo Brasil y Mercosur.',
          image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&q=80',
          link: '/transporte-rodoviario',
          color: 'from-emerald-600 to-teal-500',
          stats: [
            { icon: Zap, value: '24h', label: 'Recogida' },
            { icon: Globe, value: 'Brasil', label: '+ Mercosur' },
            { icon: Shield, value: 'GPS', label: 'Rastreo' },
          ],
          benefits: ['Puerta a puerta', 'Cargas especiales', 'DTA & MIC-DTA', 'Cross-docking'],
        },
      ],
    },
  };

  const c = content[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800 relative overflow-hidden transition-colors duration-300">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#3ECDA3]/5 to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block bg-[#3ECDA3]/10 text-[#3ECDA3] text-xs font-semibold tracking-widest px-4 py-2 rounded-full mb-6">
            {c.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight mb-4">
            {c.title}{' '}
            <span className="text-[#3ECDA3] italic">{c.titleHighlight}</span>
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-lg max-w-2xl mx-auto">
            {c.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {c.services.map((service, index) => {
            const IconComponent = service.icon;
            const isHovered = hoveredService === service.id;
            
            return (
              <div
                key={service.id}
                className={`group relative transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
                onMouseEnter={() => setHoveredService(service.id)}
                onMouseLeave={() => setHoveredService(null)}
              >
                <div className="relative h-full bg-white dark:bg-gray-900 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-700 hover:border-[#3ECDA3]/30">
                  {/* Image Header */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className={`w-full h-full object-cover transition-transform duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-80 mix-blend-multiply`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    
                    {/* Icon Badge */}
                    <div className="absolute top-4 left-4">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                    </div>

                    {/* Title on Image */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-bold text-white mb-1">{service.title}</h3>
                      <p className="text-white/80 text-sm">{service.tagline}</p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Description */}
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6">
                      {service.description}
                    </p>

                    {/* Stats Row */}
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      {service.stats.map((stat, i) => {
                        const StatIcon = stat.icon;
                        return (
                          <div key={i} className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                            <StatIcon className="w-5 h-5 text-[#3ECDA3] mx-auto mb-1" />
                            <div className="text-sm font-bold text-gray-900 dark:text-white">{stat.value}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Benefits Tags */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {service.benefits.map((benefit, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 text-xs bg-[#3ECDA3]/10 text-[#3ECDA3] px-3 py-1 rounded-full"
                        >
                          <CheckCircle className="w-3 h-3" />
                          {benefit}
                        </span>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <Link
                      to={service.link}
                      className={`group/btn w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold transition-all duration-300 ${
                        isHovered
                          ? 'bg-[#3ECDA3] text-white shadow-lg shadow-[#3ECDA3]/30'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-[#3ECDA3] hover:text-white'
                      }`}
                    >
                      {c.cta}
                      <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${isHovered ? 'translate-x-1' : 'group-hover/btn:translate-x-1'}`} />
                    </Link>
                  </div>

                  {/* Hover Glow Effect */}
                  <div className={`absolute inset-0 rounded-3xl transition-opacity duration-500 pointer-events-none ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`}>
                    <div className={`absolute inset-0 rounded-3xl bg-gradient-to-t ${service.color} opacity-5`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-16 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {language === 'pt' ? 'Não sabe qual modalidade escolher?' : language === 'en' ? "Don't know which modality to choose?" : '¿No sabe qué modalidad elegir?'}
          </p>
          <Link
            to="/contato"
            className="inline-flex items-center gap-2 text-[#3ECDA3] font-semibold hover:gap-4 transition-all duration-300"
          >
            {language === 'pt' ? 'Fale com nossos especialistas' : language === 'en' ? 'Talk to our specialists' : 'Hable con nuestros especialistas'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Services;
